import { useEffect, useEffectEvent, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';

import type { RootState } from '@a1/shared';

import useAppDataSource from 'hooks/useAppDataSource';
import {
  deriveRecentlyViewedItem,
  recentlyViewedStore,
  type RecentlyViewedItem,
  type RecentlyViewedTrackable
} from 'services/RecentlyViewedService';

export const useTrackRecentlyViewedItem = (value: RecentlyViewedTrackable) => {
  const location = useLocation();
  const { appDataSourceId } = useAppDataSource();
  const userId = useSelector((state: RootState) => state.user?.data?.id);
  const lastTrackedItemRef = useRef<string | null>(null);

  const item = useMemo(() => deriveRecentlyViewedItem({ value, pathname: location.pathname }), [location.pathname, value]);

  const recordRecentlyViewedItem = useEffectEvent((nextItem: RecentlyViewedItem) => {
    if (!userId) {
      return;
    }

    recentlyViewedStore.record({ userId, appDataSourceId, item: nextItem });
  });

  useEffect(() => {
    if (!userId || !item?.id) {
      lastTrackedItemRef.current = null;
      return;
    }

    if (lastTrackedItemRef.current === item.id) {
      return;
    }

    lastTrackedItemRef.current = item.id;
    recordRecentlyViewedItem(item);
  }, [item, userId]);
};
